import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { JSONEditorFormItem } from '@/components/ui/form-fields/JsonEditorFormItem';
import { JSONConfig } from '@/models/device.models';
import { FeatureName } from '@/models/role.models';
import hasPermission from '@/utils/permission.function';
import { Pencil } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface DeviceConfigTabProps {
  config: JSONConfig;
  onConfigChange?: (config: JSONConfig) => void;
}

export function DeviceConfigTab({
  config,
  onConfigChange,
}: DeviceConfigTabProps) {
  const [editing, setEditing] = useState<boolean>(false);
  const {
    reset,
    getValues,
    setValue,
    setError,
    control,
    formState: { isValid, isDirty },
  } = useForm<{ config: JSONConfig }>({
    defaultValues: {
      config,
    },
  });

  const undoChanges = () => {
    reset({ config: config });
    setEditing(false);
  };

  const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { config } = getValues();
    if (onConfigChange) {
      onConfigChange(config);
      setEditing(false);
      reset({ config });
    }
  };

  useEffect(() => {
    reset({ config });
    setEditing(false);
  }, [config, reset]);

  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>Config</CardTitle>
        {hasPermission(FeatureName.UPDATE_DEVICE) && (
          <Button
            type="button"
            className="absolute top-4 right-4"
            variant={'outline'}
            size={'sm'}
            onClick={() => {
              setEditing(!editing);
            }}
          >
            {editing ? (
              'Cancel Editing'
            ) : (
              <>
                <Pencil />
                Edit
              </>
            )}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {config && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void handleSubmitForm(e)}
          >
            <Controller
              name="config"
              control={control}
              render={({ field }) => (
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex-1">
                    <JSONEditorFormItem
                      onValueChange={(value) => {
                        field.onChange(value);
                        setValue('config', { ...value });
                      }}
                      value={field.value}
                      onSetError={() => {
                        setError('config', {
                          message: 'Invalid JSON!',
                        });
                      }}
                      mode={editing ? 'text' : 'view'}
                      options={{
                        navigationBar: true,
                        mainMenuBar: true,
                      }}
                    />
                  </div>
                </div>
              )}
            />

            {isDirty && (
              <CardFooter>
                <div className="mt-4 flex w-full justify-end gap-4">
                  <Button
                    variant={'outline'}
                    className="w-30"
                    size={'sm'}
                    onClick={undoChanges}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!isValid}
                    variant={'default'}
                    className="w-30"
                    type="submit"
                    size={'sm'}
                  >
                    Save
                  </Button>
                </div>
              </CardFooter>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
}
